import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { trUpper, fmtMoney, parseMoney, canonMoney } from "../lib/mw";

export default function InlineCell({ row, col, provinces, onSave }) {
  const money = col.type === "currency" || col.type === "number";
  const init = () => (money ? canonMoney(row.data[col.key]) : row.data[col.key] ?? "");
  const [val, setVal] = useState(init);
  const [districts, setDistricts] = useState([]);
  const loadedRef = useRef(false);

  useEffect(() => { setVal(init()); loadedRef.current = false; }, [row, col.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const base = "mw-inline w-full min-w-[90px] rounded border px-1.5 py-0.5 text-xs focus:outline-none";

  const loadDistricts = async (il) => {
    if (!il) { setDistricts([]); return; }
    const { data } = await api.get(`/geo/districts/${encodeURIComponent(il)}`, { silent: true });
    setDistricts(data);
  };

  // Keyboard navigation between rows of the same column (no page jump / no network).
  const moveFocus = (target, dir) => {
    const nodes = Array.from(document.querySelectorAll(`[data-col-nav="${col.key}"]`));
    const idx = nodes.indexOf(target);
    const next = nodes[idx + dir];
    if (next) setTimeout(() => { next.focus(); if (next.select) try { next.select(); } catch (e) {} }, 0);
  };

  if (col.type === "select") {
    const clr = (col.colors || {})[val];
    return (
      <select data-testid={`inline-${col.key}-${row.id}`} data-col-nav={col.key} className={base} style={clr ? { color: clr, fontWeight: 600 } : {}}
        value={val || ""} onChange={(e) => { setVal(e.target.value); onSave({ [col.key]: e.target.value }); }}>
        <option value="">—</option>
        {(col.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (col.type === "province") {
    return (
      <select data-testid={`inline-${col.key}-${row.id}`} data-col-nav={col.key} className={base} value={val || ""}
        onChange={(e) => { setVal(e.target.value); onSave({ il: e.target.value, ilce: "" }); }}>
        <option value="">—</option>
        {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
    );
  }
  if (col.type === "district") {
    return (
      <select data-testid={`inline-${col.key}-${row.id}`} data-col-nav={col.key} className={base} value={val || ""}
        onFocus={() => { if (!loadedRef.current) { loadedRef.current = true; loadDistricts(row.data.il); } }}
        onChange={(e) => { setVal(e.target.value); onSave({ [col.key]: e.target.value }); }}>
        <option value={val || ""}>{val || "—"}</option>
        {districts.filter((d) => d !== val).map((d) => <option key={d} value={d}>{d}</option>)}
      </select>
    );
  }

  const commit = () => { if ((money ? canonMoney(row.data[col.key]) : row.data[col.key] ?? "") !== val) onSave({ [col.key]: val }); };
  const transform = (v) => (col.type === "email" || col.type === "date" || col.type === "number" || col.type === "currency" ? v : trUpper(v));
  return (
    <input
      data-testid={`inline-${col.key}-${row.id}`}
      data-col-nav={col.key}
      type={col.type === "date" ? "date" : "text"}
      className={base}
      inputMode={money ? "decimal" : undefined}
      value={money ? fmtMoney(val) : val || ""}
      onChange={(e) => setVal(money ? parseMoney(e.target.value) : transform(e.target.value))}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === "ArrowDown") { e.preventDefault(); commit(); moveFocus(e.currentTarget, 1); }
        else if (e.key === "ArrowUp") { e.preventDefault(); commit(); moveFocus(e.currentTarget, -1); }
      }}
    />
  );
}
